import { ApolloError } from "@apollo/client";
import { Box, BoxProps, Divider, Flex, Text } from "@chakra-ui/react";
import React from "react";

import { Alert } from "../../../../components";
import ReadOnlyEmptyNotes from "../../../../components/Images/ReadOnlyEmptyNotes";
import {
  CallNoteFragment,
  CallNoteType,
  CallNoteV2QuestionFragment,
} from "../../../graphql";
import { ClipRange } from "../../../types";
import CallNotesSection from "../../CallNotes/CallNotesSection";
import CallQuestionNotesSection, {
  CallQuestionNotesSectionProps,
} from "../../CallNotes/CallQuestionNotesSection";
import useCurrentNote from "../../CallNotes/useCurrentNote";
import { RenderContent } from "../../utils";

export type NotesV2TabProps = BoxProps & {
  callId: string;
  clipId?: string;
  callDuration: number;
  interviewerIds: string[];
  notesReadOnly: boolean;
  questions: CallNoteV2QuestionFragment[];
  generalNotes: CallNoteFragment[];
  headerSlot?: RenderContent;
  onClickTimestamp(t: number): void;
  onAddNote(note: {
    text: string;
    type: CallNoteType;
    time: number;
    questionId?: string;
  }): Promise<void>;
  setClipRange?(c: ClipRange): void;
  playerTime: number;
  error?: ApolloError;
  loading?: boolean;
};

const NotesV2Tab: React.FC<NotesV2TabProps> = ({
  callId,
  clipId,
  callDuration,
  interviewerIds,
  notesReadOnly,
  questions,
  generalNotes,
  headerSlot,
  onClickTimestamp,
  setClipRange,
  onAddNote,
  playerTime,
  error,
  loading,
  ...rest
}) => {
  const answeredQuestions = questions.filter((q) => q.questionNotes.length);
  const unansweredQuestions = questions.filter((q) => !q.questionNotes.length);
  const currentNoteId = useCurrentNote(generalNotes, questions, playerTime);

  const showEmptyNotes =
    notesReadOnly &&
    generalNotes.length === 0 &&
    answeredQuestions.length === 0 &&
    unansweredQuestions.length === 0;

  const questionNotesProps = (
    question: CallNoteV2QuestionFragment
  ): CallQuestionNotesSectionProps => ({
    callId,
    clipId,
    callDuration,
    interviewerIds,
    question,
    playerTime,
    onAddNote,
    onClickTimestamp,
    currentNoteId,
    notesReadOnly,
    setClipRange,
  });

  if (error) {
    return (
      <Alert
        status="error"
        title="Error fetching notes"
        description={error.message}
      />
    );
  }

  if (loading) {
    return null;
  }

  if (showEmptyNotes) {
    return (
      <Flex alignItems="center" flexDir="column" pt="36">
        <ReadOnlyEmptyNotes />
        <Box fontSize="medium" textAlign="center" pt="6">
          This interview has no recorded notes from interviewers.
        </Box>
      </Flex>
    );
  }

  return (
    <Box fontSize="sm" px={{ base: "1px", md: "4" }} minH="100%" {...rest}>
      {(!notesReadOnly || generalNotes.length > 0) && (
        <Flex alignItems="center" mt="2" p="3" fontWeight="medium">
          <Text as="span">General Notes</Text>
          {headerSlot && <Box ml="auto">{headerSlot}</Box>}
        </Flex>
      )}

      {/* General notes */}
      <CallNotesSection
        callId={callId}
        clipId={clipId}
        callDuration={callDuration}
        interviewerIds={interviewerIds}
        notes={generalNotes}
        playerTime={playerTime}
        onAddNote={onAddNote}
        onClickTimestamp={onClickTimestamp}
        setClipRange={setClipRange}
        currentNoteId={currentNoteId}
        notesReadOnly={notesReadOnly}
      />

      {answeredQuestions.map((question) => (
        <CallQuestionNotesSection
          key={question.id}
          {...questionNotesProps(question)}
        />
      ))}

      {unansweredQuestions.length > 0 && (
        <>
          <Divider my="3" />

          {unansweredQuestions.map((question) => (
            <CallQuestionNotesSection
              key={question.id}
              {...questionNotesProps(question)}
            />
          ))}
        </>
      )}
    </Box>
  );
};

NotesV2Tab.displayName = "NotesV2Tab";

export default React.memo(NotesV2Tab);
